import { useMemo, useState } from "react";
import {
  Badge,
  Box,
  Button,
  Flex,
  HStack,
  Icon,
  Input,
  InputGroup,
  InputLeftElement,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Portal,
  Spinner,
  Text,
  useColorModeValue,
} from "@chakra-ui/react";
import { ChevronDown, Search, Check } from "lucide-react";

export interface AvailableKey {
  key: string;
  label: string;
  mission_title: string;
}

interface FieldKeySelectorProps {
  value: string;
  keysByMission: Record<string, AvailableKey[]>;
  onChange: (val: string | null) => void;
  isLoading?: boolean;
  placeholder?: string;
}

export const FieldKeySelector = ({
  value,
  keysByMission,
  onChange,
  isLoading = false,
  placeholder = "— nenhum campo selecionado —",
}: FieldKeySelectorProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");

  const allKeys     = useMemo(() => Object.values(keysByMission).flat(), [keysByMission]);
  const selectedKey = allKeys.find(k => k.key === value);

  const popoverBg        = useColorModeValue("white", "#132035");
  const popoverBorder    = useColorModeValue("rgba(0,0,0,0.08)", "rgba(255,255,255,0.08)");
  const triggerBg        = useColorModeValue("white", "rgba(255,255,255,0.05)");
  const defaultBorder    = useColorModeValue("rgba(0,0,0,0.15)", "rgba(255,255,255,0.15)");
  const triggerBorder    = value ? "#009efb" : defaultBorder;
  const labelColor       = useColorModeValue("#434347", "#e8edf2");
  const mutedColor       = useColorModeValue("#84858c", "#5a7494");
  const hoverBg          = useColorModeValue("rgba(0,158,251,0.06)", "#1a2d47");
  const selectedRowBg    = "rgba(0,158,251,0.12)";
  const groupColor       = useColorModeValue("#84858c", "#5a7494");
  const keyBadgeBg       = useColorModeValue("rgba(0,0,0,0.06)", "rgba(255,255,255,0.08)");
  const keyBadgeColor    = useColorModeValue("#696a71", "#8fa3b8");
  const selKeyBadgeBg    = "rgba(0,158,251,0.20)";
  const selKeyBadgeColor = "#009efb";
  const dividerColor     = useColorModeValue("rgba(0,0,0,0.06)", "rgba(255,255,255,0.06)");
  const boxShadow        = useColorModeValue("0px 4px 8px rgba(14,61,93,0.12)", "0px 4px 16px rgba(0,0,0,0.5)");

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    if (!q) return keysByMission;
    const out: Record<string, AvailableKey[]> = {};
    Object.entries(keysByMission).forEach(([mission, keys]) => {
      const hits = keys.filter(k =>
        k.label.toLowerCase().includes(q) || k.key.toLowerCase().includes(q)
      );
      if (hits.length) out[mission] = hits;
    });
    return out;
  }, [search, keysByMission]);

  const hasResults = Object.keys(filtered).length > 0;

  const close  = () => { setIsOpen(false); setSearch(""); };
  const select = (key: string | null) => { onChange(key); close(); };

  return (
    <Popover isOpen={isOpen} onClose={close} placement="bottom-start" matchWidth isLazy closeOnBlur>
      <PopoverTrigger>
        <Button
          w="full"
          h="40px"
          variant="outline"
          bg={triggerBg}
          borderColor={triggerBorder}
          borderRadius="8px"
          justifyContent="space-between"
          fontWeight="normal"
          onClick={() => setIsOpen(o => !o)}
          _hover={{ borderColor: "#009efb", boxShadow: "0 0 0 2px rgba(0,158,251,0.15)" }}
          _focus={{ borderColor: "#009efb", boxShadow: "0 0 0 2px rgba(0,158,251,0.15)" }}
          px={3}
          isDisabled={isLoading}
          rightIcon={isLoading ? <Spinner size="xs" /> : undefined}
        >
          {isLoading ? (
            <Text fontSize="xs" color={mutedColor} flex={1} textAlign="left">Carregando campos...</Text>
          ) : selectedKey ? (
            <HStack spacing={2} overflow="hidden" flex={1} minW={0}>
              <Badge
                bg={selKeyBadgeBg}
                color={selKeyBadgeColor}
                fontFamily="mono"
                fontSize="10px"
                fontWeight="700"
                letterSpacing="0.8px"
                textTransform="uppercase"
                px={1.5}
                py={0.5}
                borderRadius="4px"
                border="1px solid rgba(0,158,251,0.3)"
                flexShrink={0}
              >
                {selectedKey.key}
              </Badge>
              <Text fontSize="xs" noOfLines={1} color={labelColor} textAlign="left">
                {selectedKey.label}
              </Text>
            </HStack>
          ) : (
            <Text fontSize="xs" color={mutedColor} flex={1} textAlign="left">{placeholder}</Text>
          )}
          {!isLoading && <Icon as={ChevronDown} boxSize={3} color={mutedColor} ml={2} flexShrink={0} />}
        </Button>
      </PopoverTrigger>

      <Portal>
        <PopoverContent
          bg={popoverBg}
          borderColor={popoverBorder}
          borderRadius="12px"
          boxShadow={boxShadow}
          _focus={{ outline: "none" }}
          overflow="hidden"
        >
          <PopoverBody p={0}>
            <Box p={2} borderBottomWidth="1px" borderColor={dividerColor}>
              <InputGroup size="sm">
                <InputLeftElement pointerEvents="none" pl={0.5}>
                  <Icon as={Search} boxSize={3} color={mutedColor} />
                </InputLeftElement>
                <Input
                  placeholder="Buscar campo ou chave..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  borderRadius="lg"
                  border="none"
                  bg="transparent"
                  color={labelColor}
                  _placeholder={{ color: mutedColor }}
                  _focus={{ boxShadow: "none" }}
                  autoFocus
                  fontSize="xs"
                />
              </InputGroup>
            </Box>

            <Box maxH="280px" overflowY="auto" py={1}>
              <Flex
                px={3} py={2}
                cursor="pointer"
                align="center"
                borderRadius="lg"
                mx={1}
                _hover={{ bg: hoverBg }}
                onClick={() => select(null)}
              >
                <Text fontSize="xs" color={mutedColor} fontStyle="italic">— nenhum campo —</Text>
              </Flex>

              {!hasResults ? (
                <Flex justify="center" py={4}>
                  <Text fontSize="xs" color={mutedColor}>Nenhum resultado para "{search}"</Text>
                </Flex>
              ) : (
                Object.entries(filtered).map(([missionTitle, keys], gi) => (
                  <Box key={missionTitle} mt={gi === 0 ? 0 : 1}>
                    <Text
                      fontSize="10px"
                      fontWeight="700"
                      color={groupColor}
                      textTransform="uppercase"
                      letterSpacing="0.07em"
                      px={4}
                      pt={2}
                      pb={1}
                    >
                      {missionTitle}
                    </Text>
                    {keys.map(k => {
                      const isSel = k.key === value;
                      return (
                        <Flex
                          key={k.key}
                          px={3} py={2}
                          mx={1}
                          borderRadius="lg"
                          cursor="pointer"
                          align="center"
                          gap={2.5}
                          bg={isSel ? selectedRowBg : "transparent"}
                          _hover={{ bg: isSel ? selectedRowBg : hoverBg }}
                          onClick={() => select(k.key)}
                        >
                          <Badge
                            fontFamily="mono"
                            fontSize="10px"
                            fontWeight="700"
                            letterSpacing="0.6px"
                            textTransform="uppercase"
                            bg={isSel ? selKeyBadgeBg : keyBadgeBg}
                            color={isSel ? selKeyBadgeColor : keyBadgeColor}
                            px={1.5}
                            py={0.5}
                            borderRadius="4px"
                            border={isSel ? "1px solid rgba(0,158,251,0.3)" : "1px solid rgba(255,255,255,0.06)"}
                            flexShrink={0}
                          >
                            {k.key}
                          </Badge>
                          <Text fontSize="xs" color={labelColor} flex={1} noOfLines={1}>
                            {k.label}
                          </Text>
                          {isSel && <Icon as={Check} boxSize={3} color="#009efb" flexShrink={0} />}
                        </Flex>
                      );
                    })}
                  </Box>
                ))
              )}
            </Box>
          </PopoverBody>
        </PopoverContent>
      </Portal>
    </Popover>
  );
};
